import {format} from 'date-fns'
import {FieldError, Form, Input, Label, TextField} from 'react-aria-components'

import InfoSvg from '@/assets/icons/info.svg?react'
import lockSvg from '@/assets/icons/lock.svg'
import PriceHistorySvg from '@/assets/icons/price-history.svg?react'
import stripeTrustBadgePng from '@/assets/images/stripe-trust-badge.png'
import useIsAuthenticated from '@/auth/hooks/useIsAuthenticated'
import Price from '@/components/Price'
import PrimaryButton from '@/components/PrimaryButton/PrimaryButton'
import Switch from '@/components/Switch/Switch'
import {USD_DISPLAY_DECIMALS} from '@/constants/config'
import {FLIGHT_TYPE_MAP} from '@/lib/flight/constants'
import type {FlightBooking} from '@/lib/flight/flightModel'
import {useRoundUp} from '@/lib/payment/store'
import {FlightDetailsRoute} from '@/routeRegistry'
import VisuallyHidden from '@/utils/a11y/VisuallyHidden'
import useSearch from '@/utils/router/useSearch'
import style from '@/views/Flight/FlightDetails.module.scss'
import useFlightSummary from '@/views/Flight/hooks/useFightSummary'

import ExpressFlightCheckout from './ExpressFlightCheckout'

interface Props {
  onSubmit: () => void
  isSubmitting: boolean
  submitFlightBooking: () => Promise<FlightBooking | undefined>
}

export default memo(function FlightSummary({onSubmit, isSubmitting, submitFlightBooking}: Props) {
  const {flightId} = FlightDetailsRoute.useParams()

  const isAuthenticated = useIsAuthenticated()
  const {option, step} = FlightDetailsRoute.useSearch()
  const [applyRewards, setApplyRewards] = useSearch(FlightDetailsRoute.id, 'applyRewards')
  const [coupon, setCoupon] = useSearch(FlightDetailsRoute.id, 'coupon')
  const {
    flightDetails,
    discountValue,
    total,
    cashback,
    handleCouponBlur,
    couponError,
    handleCouponChange,
    couponInfo,
    isCouponLoading,
    couponIsDirty,
    roundUpAmount,
  } = useFlightSummary(flightId, option, applyRewards, coupon, setCoupon)
  const [roundUp, setRoundUp] = useRoundUp()

  const isCouponValid = couponError === null

  if (!flightDetails) return null

  const passengerText = (() => {
    const texts = []

    if (flightDetails.metadata.adults > 0)
      texts.push(
        `${flightDetails.metadata.adults} adult${flightDetails.metadata.adults > 1 ? 's' : ''}`,
      )
    if (flightDetails.metadata.children > 0)
      texts.push(
        `${flightDetails.metadata.children} child${flightDetails.metadata.children > 1 ? 'ren' : ''}`,
      )
    if (flightDetails.metadata.infants_in_seat > 0)
      texts.push(
        `${flightDetails.metadata.infants_in_seat} infant${
          flightDetails.metadata.infants_in_seat > 1 ? 's' : ''
        } in seat`,
      )
    if (flightDetails.metadata.infants_on_lap > 0)
      texts.push(
        `${flightDetails.metadata.infants_on_lap} infant${
          flightDetails.metadata.infants_on_lap > 1 ? 's' : ''
        } on lap`,
      )
    return texts.join(', ')
  })()

  const passengerNumber =
    flightDetails.metadata.adults +
    flightDetails.metadata.children +
    flightDetails.metadata.infants_in_seat +
    flightDetails.metadata.infants_on_lap
  const passengerNumberText = `${passengerNumber} passenger${passengerNumber > 1 ? 's' : ''}`

  return (
    <div className={style.TicketSummary}>
      <div className={style.TicketSummaryContent}>
        <div className={style.Header} role='heading' aria-level={2}>
          Ticket summary
        </div>
        <div className={style.Ticket}>
          <div className={style.TicketHeader}>
            <div className={style.Summary}>
              <VisuallyHidden>Flight type: </VisuallyHidden>
              {FLIGHT_TYPE_MAP[flightDetails.metadata.type]},
              <VisuallyHidden>Passengers: </VisuallyHidden> {passengerText}
            </div>
            <div className={style.TicketHeaderTitle}>
              <VisuallyHidden>Flight route: from</VisuallyHidden>
              {flightDetails.metadata.departure_city} ({flightDetails.metadata.departure_id}) to{' '}
              {flightDetails.metadata.arrival_city} ({flightDetails.metadata.arrival_id})
            </div>
            <div className={style.TicketHeaderDescription}>
              {/* TODO: support multi-city */}
              <VisuallyHidden>Departure time: </VisuallyHidden>
              {format(
                new Date(
                  flightDetails.selected_flights[0]?.flights[0]?.departure_airport.time ?? 0,
                ),
                'MMM dd, yyyy',
              )}{' '}
              - <VisuallyHidden as='br' />
              <VisuallyHidden>Arrival time: </VisuallyHidden>
              {format(
                new Date(
                  flightDetails.selected_flights[flightDetails.selected_flights.length - 1]
                    ?.flights[
                    (flightDetails.selected_flights[flightDetails.selected_flights.length - 1]
                      ?.flights.length ?? 1) - 1
                  ]?.arrival_airport.time ?? 0,
                ),
                'MMM dd, yyyy',
              )}
            </div>
          </div>
          <div className={style.TicketPriceItemWrapper}>
            <div className={style.TicketPriceItem}>
              <div className={style.TicketPriceItemTitle}>Subtotal ({passengerNumberText})</div>
              <div className={style.TicketPriceItemPrice}>
                <Price
                  price={flightDetails.booking_options[Number(option)]?.together?.price}
                  options={{
                    fractionDigits: 2,
                    exactFractionDigits: true,
                  }}
                />
              </div>
            </div>
            {roundUpAmount.gt(0) && (
              <div className={clsx(style.TicketPriceItem, style.Secondary)}>
                <div className={style.SwitchWrapper}>
                  <Switch
                    label='Donate change to NFB'
                    onChange={setRoundUp}
                    isSelected={roundUp}
                    isLabelVisible
                    labelClassName={style.TicketPriceItemTitle}
                  />
                  <a
                    href='https://blog.innosearch.ai/'
                    className={style.Info}
                    target='_blank'
                    rel='noopener noreferrer'
                  >
                    <InfoSvg aria-label='Read more about NFB change donation program' />
                  </a>
                </div>
                <span
                  className={clsx(
                    style.TicketPriceItemPrice,
                    roundUp ? style.RoundedUp : style.UnRounded,
                  )}
                >
                  <Price
                    price={roundUpAmount}
                    options={{
                      fractionDigits: USD_DISPLAY_DECIMALS,
                      exactFractionDigits: true,
                    }}
                  />
                </span>
              </div>
            )}
            {step < 3 && isAuthenticated && discountValue > 0 && (
              <div className={style.TicketPriceItem}>
                <Switch
                  label='Apply rewards'
                  onChange={setApplyRewards}
                  isSelected={applyRewards}
                  isLabelVisible
                  labelClassName={style.TicketPriceItemTitle}
                />
                {applyRewards && (
                  <div
                    className={clsx(
                      style.TicketPriceItemPrice,
                      discountValue > 0 && style.GoodValue,
                    )}
                  >
                    <Price
                      price={discountValue === 0 ? discountValue : -discountValue}
                      options={{
                        fractionDigits: 2,
                        exactFractionDigits: true,
                      }}
                    />
                  </div>
                )}
              </div>
            )}
            {step >= 3 && applyRewards && discountValue > 0 && (
              <div className={style.TicketPriceItem}>
                <div className={clsx(style.TicketPriceItemTitle, style.GoodValue)}>
                  Rewards applied
                </div>
                <div
                  className={clsx(style.TicketPriceItemPrice, discountValue > 0 && style.GoodValue)}
                >
                  <Price
                    price={discountValue === 0 ? discountValue : -discountValue}
                    options={{
                      fractionDigits: 2,
                      exactFractionDigits: true,
                    }}
                  />
                </div>
              </div>
            )}
            {step < 3 && (
              <VisuallyHidden
                as={Form}
                className={style.CouponForm}
                onSubmit={e => {
                  e.preventDefault()
                }}
                isVisible={!applyRewards}
              >
                <TextField
                  name='coupon'
                  defaultValue={coupon}
                  onChange={handleCouponChange}
                  onBlur={handleCouponBlur}
                  validationBehavior='aria'
                  isInvalid={!isCouponValid}
                  type='text'
                  className={style.Field}
                  isDisabled={applyRewards}
                >
                  <VisuallyHidden as={Label}>Coupon code</VisuallyHidden>
                  <Input className={style.Input} placeholder='Coupon code' />
                  {couponError && (
                    <FieldError className={style.Error}>{couponError.message}</FieldError>
                  )}
                </TextField>
                <PrimaryButton
                  type='submit'
                  size='sm'
                  className={style.ApplyBtn}
                  isPending={isCouponLoading}
                  isDisabled={!couponIsDirty}
                >
                  Apply
                </PrimaryButton>
              </VisuallyHidden>
            )}
            {couponInfo && !applyRewards && (
              <div className={style.CouponInfo}>
                <div>Coupon applied</div>
                <div>
                  <Price
                    price={-couponInfo.amount}
                    options={{
                      fractionDigits: USD_DISPLAY_DECIMALS,
                      exactFractionDigits: true,
                    }}
                  />
                </div>
              </div>
            )}
          </div>
          <div className={style.Divider} />
          <div className={style.TicketTotalWrapper}>
            <div className={style.TicketTotal}>
              <div className={style.TicketTotalTitle}>
                Total <VisuallyHidden>&nbsp;price: &nbsp;&nbsp;</VisuallyHidden>
              </div>
              <div className={style.TicketTotalPrice}>
                <Price
                  price={total}
                  options={{
                    fractionDigits: USD_DISPLAY_DECIMALS,
                    exactFractionDigits: true,
                  }}
                />
              </div>
            </div>
            {cashback && (
              <div className={clsx(style.TicketTotal, style.GoodValue, style.Mini)}>
                <div className={style.TicketTotalTitle}>
                  <PriceHistorySvg className={style.Decoration} aria-hidden />
                  Rewards ({cashback.rate.mul(100).toNumber()}%)
                </div>
                <div className={style.TicketTotalPrice}>
                  <Price
                    price={cashback.value}
                    options={{
                      fractionDigits: USD_DISPLAY_DECIMALS,
                      exactFractionDigits: true,
                    }}
                  />
                </div>
              </div>
            )}
          </div>
          {step === 5 && <img src={stripeTrustBadgePng} alt='' />}
          <div className={style.Buttons}>
            {step === 3 && (
              <ExpressFlightCheckout
                submitFlightBooking={submitFlightBooking}
                className={style.ExpressCheckout}
              />
            )}
            <PrimaryButton
              className={style.ContinueButton}
              onPress={onSubmit}
              isPending={isSubmitting}
            >
              {(() => {
                if (step === 3) return 'Pay with Card'
                if (step < 5) return 'Next step'
                return (
                  <>
                    {!isSubmitting && <img src={lockSvg} alt='' />}
                    {!isSubmitting ? 'Pay now' : 'Paying...'}
                  </>
                )
              })()}
            </PrimaryButton>
          </div>
        </div>
      </div>
    </div>
  )
})
